import { emptySplitApi } from "./empty-split-api";
import {
  BulkSetInsurersActiveArgs,
  BulkSetInsurersActivePreview,
  BulkSetInsurersActiveResult,
  Insurer,
  InsurerCreate,
  InsurerUpdate,
  InsurersCountResponse,
  InsurersFilters,
  InsurersResponse
} from "./types/insurers.type";

// Settings > Insurers asks for each insurer's policy count at this practice (a per-row COUNT the
// backend computes only when the query names it).
export const INSURERS_WITH_POLICY_COUNT_QUERY = "{*,practice_policy_count}";

// The list filters go in the query string and are only read for `select_all`, so they are sent
// exactly as the list sends them and the change hits the rows the user was looking at.
export const bulkSetInsurersActiveRequest = (
  { is_active, ...scope }: BulkSetInsurersActiveArgs,
  dryRun: boolean
) => {
  const dry_run = dryRun || undefined;
  if (!("select_all" in scope)) {
    return {
      url: "/insurers/bulk-set-active/",
      method: "POST",
      body: { is_active, insurer_ids: scope.insurer_ids, dry_run }
    };
  }
  const {
    exclude_ids,
    filters: { search, ...filters }
  } = scope;
  return {
    url: "/insurers/bulk-set-active/",
    method: "POST",
    params: { ...filters, search: search && encodeURIComponent(search) },
    body: { is_active, select_all: true, exclude_ids, dry_run }
  };
};

export const insurersApi: any = emptySplitApi.injectEndpoints({
  endpoints: (builder) => ({
    getInsurers: builder.query<InsurersResponse, InsurersFilters>({
      query: ({
        limit = 300,
        offset = 0,
        query = "{*}",
        search,
        ...params
      }) => ({
        url: `/insurers/`,
        params: {
          limit,
          offset,
          query,
          search: search && encodeURIComponent(search),
          ...params
        }
      }),
      providesTags: ["Insurers"]
    }),
    getInsurersCount: builder.query<InsurersCountResponse, InsurersFilters>({
      query: ({
        query = "{*}",
        search,
        limit: _limit,
        offset: _offset,
        ...params
      }) => ({
        url: `/insurers/count/`,
        params: {
          query,
          search: search && encodeURIComponent(search),
          ...params
        }
      }),
      providesTags: ["Insurers"]
    }),
    createInsurer: builder.mutation<Insurer, InsurerCreate>({
      query: (body) => ({
        url: "/insurers/",
        method: "POST",
        body: { ...body, phone: body.phone?.replace(/\D/g, "") }
      }),
      invalidatesTags: ["Insurers"]
    }),
    updateInsurer: builder.mutation<Insurer, InsurerUpdate>({
      query: ({ id, ...body }) => ({
        url: `/insurers/${id}/`,
        method: "PATCH",
        body: { ...body, phone: body.phone?.replace(/\D/g, "") }
      }),
      invalidatesTags: ["Insurers"]
    }),
    deleteInsurer: builder.mutation<Insurer, { id: number }>({
      query: ({ id }) => ({
        url: `/insurers/${id}/`,
        method: "DELETE"
      }),
      invalidatesTags: ["Insurers"]
    }),
    previewBulkSetInsurersActive: builder.mutation<
      BulkSetInsurersActivePreview,
      BulkSetInsurersActiveArgs
    >({
      query: (args) => bulkSetInsurersActiveRequest(args, true)
    }),
    bulkSetInsurersActive: builder.mutation<
      BulkSetInsurersActiveResult,
      BulkSetInsurersActiveArgs
    >({
      query: (args) => bulkSetInsurersActiveRequest(args, false),
      invalidatesTags: ["Insurers", "BulkActions"]
    })
  })
});

export const {
  useGetInsurersQuery,
  useGetInsurersCountQuery,
  useDeleteInsurerMutation,
  useCreateInsurerMutation,
  useUpdateInsurerMutation,
  usePreviewBulkSetInsurersActiveMutation,
  useBulkSetInsurersActiveMutation
} = insurersApi;
